import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const RupeeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h12M6 9h12M12 9l-6 11M6 9a5 5 0 000 0h4a4 4 0 000-5H6" />
  </svg>
);
const UsersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5-3.87M9 20H4v-2a4 4 0 015-3.87m6-4.13a4 4 0 10-8 0 4 4 0 008 0zm6 0a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const GiftIcon = () => (
  // <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
  //   <path strokeLinecap="round" strokeLinejoin="round" d="M20 12v10H4V12M2 7h20v5H2zM12 22V7m0 0a2 2 0 10-2-2m2 2a2 2 0 112-2" />
  // </svg>
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a4 4 0 010 8H9m-6-8l3-3m-3 3l3 3" />
  </svg>
);
const RefundIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a4 4 0 010 8H9m-6-8l3-3m-3 3l3 3" />
  </svg>
);
const ArrowUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 4l8 8H4z" />
  </svg>
);
const ArrowDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 20l-8-8h16z" />
  </svg>
);

const StatCard = ({ label, value, changePercent, icon, accentColor }) => {
  const accent = {
    blue:   { border: "#3B82F6", label: "#3B82F6", icon: "#93C5FD" },
    green:  { border: "#10B981", label: "#10B981", icon: "#6EE7B7" },
    teal:   { border: "#06B6D4", label: "#06B6D4", icon: "#67E8F9" },
    yellow: { border: "#F59E0B", label: "#F59E0B", icon: "#FCD34D" },
  }[accentColor] || { border: "#6B7280", label: "#6B7280", icon: "#D1D5DB" };

  const hasChange = changePercent !== null && changePercent !== undefined;
  const isPositive = changePercent >= 0;

  return (
    <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #E5E7EB", borderLeft: `4px solid ${accent.border}`, padding: "20px 22px", display: "flex", flexDirection: "column", gap: "6px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "18px", right: "18px", color: accent.icon }}>{icon}</div>
      <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: accent.label }}>{label}</div>
      <div style={{ fontSize: "28px", fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>{value ?? "—"}</div>
      {hasChange ? (
        <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 600, color: isPositive ? "#10B981" : "#EF4444" }}>
          {isPositive ? <ArrowUp /> : <ArrowDown />}
          {Math.abs(changePercent)}% from last month
        </div>
      ) : (
        <div style={{ fontSize: "12px", color: "#D1D5DB" }}>Overall data</div>
      )}
    </div>
  );
};

const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.04) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const PIE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444"];

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/dashboard");
      setDashboardData(res.data?.data || {});
    } catch (e) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboardData(); }, []);

  if (loading) return <div style={{ padding: "40px", color: "#6B7280", fontSize: "14px" }}>Loading...</div>;

  const fmt = (val) => (val != null ? `₹${Number(val).toLocaleString("en-IN")}` : "—");
  const changes = dashboardData?.changes || {};
  const graphs  = dashboardData?.graphs  || {};

  const lineData = (graphs.userRegistrations?.labels || []).map((label, i) => ({
    month: label,
    users: graphs.userRegistrations?.data?.[i] ?? 0,
  }));

  const pieData = graphs.bookingPie || [];

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ background: "#F9FAFB", minHeight: "100vh" }}>
      <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#111827", margin: 0 }}>Dashboard</h1>
      <p style={{ fontSize: "13px", color: "#9CA3AF", marginTop: "4px" }}>Overview and stats</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <StatCard label="Booking Total Processed Amount" value={fmt(dashboardData?.totalPayments)} changePercent={null} icon={<RupeeIcon />} accentColor="blue" />
        <StatCard label="Total Users" value={dashboardData?.totalUsers ?? "—"} changePercent={null} icon={<UsersIcon />} accentColor="green" />
        <StatCard label="Total Giftcard Refunds" value={fmt(dashboardData?.totalGiftcardRefunds)} changePercent={null} icon={<GiftIcon />} accentColor="teal" />
        <StatCard label="Total Refunds" value={fmt(dashboardData?.totalRefunds)} changePercent={null} icon={<RefundIcon />} accentColor="yellow" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-7">

        <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #E5E7EB", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} className="min-w-0">
          <div style={{ fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "4px" }}>User Registrations</div>
          <div style={{ fontSize: "11px", color: "#9CA3AF", marginBottom: "20px" }}>Last 6 months</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={lineData} margin={{ top: 4, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: "8px", border: "1px solid #E5E7EB", fontSize: "12px" }}
                formatter={(val) => [val, "New Users"]}
              />
              <Line type="monotone" dataKey="users" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4, fill: "#10B981", strokeWidth: 0 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #E5E7EB", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} className="min-w-0">
          <div style={{ fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "4px" }}>Amount Breakdown</div>
          <div style={{ fontSize: "11px", color: "#9CA3AF", marginBottom: "10px" }}>Booking refunds (₹)</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={85} dataKey="value" labelLine={false} label={renderCustomLabel} >
                {pieData.map((_, index) => (
                  <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: "8px", border: "1px solid #E5E7EB", fontSize: "12px" }}
                formatter={(val) => [`₹${Number(val).toLocaleString("en-IN")}`, ""]}
              />
              <Legend iconType="circle" iconSize={8}  wrapperStyle={{ fontSize: "11px", color: "#6B7280" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

      </div>
    </div>
  );
}